import 'react-multi-carousel/lib/styles.css'

import { Trans } from '@lingui/macro'
import { ButtonPrimary } from 'components/Button'
import { DarkCard } from 'components/Card'
import { ColumnCenter } from 'components/Column'
import Row, { RowBetween, RowCenter } from 'components/Row'
import React, { useMemo, useState } from 'react'
import { AlertTriangle } from 'react-feather'
import NumberFormat from 'react-number-format'
import { useDarkModeManager } from 'state/user/hooks'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'
import { tokenToDecimals } from 'utils/tokens'
import { isMobile } from 'utils/userAgent'

import * as StyleSport from '../style'
import { SportBetModal } from './BetModal'
import Filter from './Filter'

const Container = styled.div`
  width: 100%;
  margin-top: 1rem;
`

const Message = styled.div`
  width: 40%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
  margin-top: 2rem;
  border 1px solid #FF8F00;
  border-radius: 16px;
  padding: 8px;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
  `};
`

const ResponsiveGrid = styled.div<{ isMobile?: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '100%' : '50% 50%')};
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-direction: column;
    align-items: flex-start;
    row-gap: 16px;
    width: 100%:
  `};
`

const PageButtons = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
`

const Arrow = styled.div`
  color: ${({ theme }) => theme.text1};
  padding: 0 20px;
  :hover {
    cursor: pointer;
  }
`

export const calculateRatio = (volumen: string, base: string) => {
  const currentRatio = tokenToDecimals(volumen || '0', 18) / tokenToDecimals(base || '0', 18)
  if (currentRatio === Infinity) {
    return 0
  }
  return currentRatio || 0
}

interface ISportProvider {
  id: string
  leagueName: string
  league: string
  sportType: string
}

interface IAcornSportBets {
  player: string
}

interface IAcornSportEvents {
  id: string
  gameIndex: string
  homeTeam: string
  homeScore?: number
  awayTeam: string
  awayScore?: number
  statusId?: number
  startTime: string
  volumen?: string
  volumenHome: string
  volumenAway: string
  volumenDraw: string
  provider: ISportProvider
  bets?: IAcornSportBets[]
}

interface EventsProps {
  bets: IAcornSportEvents[]
}

function Events({ bets }: EventsProps) {
  const [isDark] = useDarkModeManager()
  const [betModalIsOpen, setBetModalIsOpen] = useState(false)
  const [scheduleItem, setScheduleItem] = useState<IAcornSportEvents | undefined>()
  const [betPosition, setBetPosition] = useState<number>(0)
  const [page, setPage] = useState(1)
  const [currentSportId, setCurrentSportId] = useState<number | undefined>()
  const [showFinished, setShowFinished] = useState(false)

  const onChangeSport = (sportId?: number) => {
    setCurrentSportId(sportId)
    setPage(1)
  }

  const onShowFinished = (show: boolean) => {
    setShowFinished(show)
    setPage(1)
  }

  const openModalBet = (item: IAcornSportEvents, position: number) => {
    setBetPosition(position)
    setScheduleItem(item)
    setBetModalIsOpen(true)
  }

  const closeBetModal = () => {
    setBetModalIsOpen(false)
  }

  const ITEMS = 10

  const activeBets = bets
    ?.sort((a, b) =>
      a.startTime && b.startTime
        ? showFinished
          ? parseInt(b.startTime) - parseInt(a.startTime)
          : parseInt(a.startTime) - parseInt(b.startTime)
        : 0
    )
    .filter((item) =>
      showFinished
        ? Number(item.startTime) + 7200 <= new Date().getTime() / 1000
        : Number(item.startTime) + 7200 > new Date().getTime() / 1000
    )

  const betSports = useMemo(() => {
    if (!currentSportId) return activeBets
    return activeBets.filter((activeBets) => Number(activeBets.provider.sportType) === currentSportId)
  }, [activeBets, currentSportId])

  const sortedBets = useMemo(() => {
    return betSports.length > 0 ? betSports.slice(ITEMS * (page - 1), page * ITEMS) : []
  }, [betSports, page])

  const maxPage = sortedBets.length > 0 ? Math.ceil(betSports.length / ITEMS) : 0

  return (
    <>
      <StyleSport.SectionTypesSports>
        <Filter onChangeSport={onChangeSport} onShowFinished={onShowFinished} />
      </StyleSport.SectionTypesSports>
      <Container>
        {betSports && betSports.length > 0 ? (
          <Container>
            <ResponsiveGrid isMobile={isMobile}>
              {sortedBets.map((item: IAcornSportEvents, index) => (
                <DarkCard key={index} isDark={isDark} width={isMobile ? '99%' : '95%'} minHeight="260px" mb="1rem">
                  <RowCenter minHeight="25px">
                    <img
                      width="64px"
                      src={
                        isDark
                          ? `${process.env.PUBLIC_URL}/images/sports/leagues/${item?.provider.sportType}_White.png`
                          : `${process.env.PUBLIC_URL}/images/sports/leagues/${item?.provider.sportType}.png`
                      }
                      alt=""
                    />
                  </RowCenter>
                  <RowBetween minHeight="130px" mt="1rem">
                    <ColumnCenter>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/sports/tshirts/${item.provider.sportType}/${item.homeTeam}_Home.svg`}
                        alt="home"
                        width="64px"
                      />
                      <ThemedText.Body textAlign="center">{item.homeTeam}</ThemedText.Body>
                    </ColumnCenter>
                    <ColumnCenter>
                      {item.homeScore && item.awayScore ? (
                        <Row justify="center">
                          <ThemedText.LargeHeader color="blue1" textAlign="center">
                            {item.homeScore}
                          </ThemedText.LargeHeader>
                          <ThemedText.LargeHeader color="blue1" textAlign="center">
                            :
                          </ThemedText.LargeHeader>
                          <ThemedText.LargeHeader color="blue1" textAlign="center">
                            {item.awayScore}
                          </ThemedText.LargeHeader>
                        </Row>
                      ) : (
                        <ThemedText.MediumHeader textAlign="center">VS</ThemedText.MediumHeader>
                      )}
                      <ThemedText.Italic textAlign="center">
                        {new Date(Number(item.startTime) * 1000).toLocaleDateString()}
                      </ThemedText.Italic>
                      <ThemedText.Italic textAlign="center">
                        {new Date(Number(item.startTime) * 1000).toLocaleTimeString()}
                      </ThemedText.Italic>
                    </ColumnCenter>
                    <ColumnCenter>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/sports/tshirts/${item.provider.sportType}/${item.awayTeam}_Away.svg`}
                        alt="away"
                        width="64px"
                      />
                      <ThemedText.Body textAlign="center">{item.awayTeam}</ThemedText.Body>
                    </ColumnCenter>
                  </RowBetween>
                  <RowBetween minHeight="10px" mt="1rem">
                    <ButtonPrimary
                      width="30%"
                      size="10px"
                      disabled={Number(item.startTime) <= Math.floor(Date.now() / 1000)}
                      onClick={() => openModalBet(item, 0)}
                    >
                      <NumberFormat
                        value={calculateRatio(item.volumen || '0', item.volumenHome || '0')}
                        suffix="x"
                        decimalScale={2}
                        fixedDecimalScale
                        displayType="text"
                      />
                    </ButtonPrimary>
                    <ButtonPrimary
                      width="30%"
                      size="10px"
                      disabled={Number(item.startTime) <= Math.floor(Date.now() / 1000)}
                      onClick={() => openModalBet(item, 0)}
                    >
                      <NumberFormat
                        value={calculateRatio(item.volumen || '0', item.volumenDraw || '0')}
                        suffix="x"
                        decimalScale={2}
                        fixedDecimalScale
                        displayType="text"
                      />
                    </ButtonPrimary>
                    <ButtonPrimary
                      width="30%"
                      size="10px"
                      disabled={Number(item.startTime) <= Math.floor(Date.now() / 1000)}
                      onClick={() => openModalBet(item, 0)}
                    >
                      <NumberFormat
                        value={calculateRatio(item.volumen || '0', item.volumenAway || '0')}
                        suffix="x"
                        decimalScale={2}
                        fixedDecimalScale
                        displayType="text"
                      />
                    </ButtonPrimary>
                  </RowBetween>
                </DarkCard>
              ))}
            </ResponsiveGrid>
            <PageButtons>
              <Arrow
                onClick={() => {
                  setPage(page === 1 ? page : page - 1)
                }}
              >
                <ThemedText.Body color={page === 1 ? 'text' : 'primary'}> {'<'} </ThemedText.Body>
              </Arrow>

              <ThemedText.Body>
                <Trans>Page</Trans> {page} <Trans>of</Trans> {maxPage}
              </ThemedText.Body>
              <Arrow
                onClick={() => {
                  setPage(page === maxPage ? page : page + 1)
                }}
              >
                <ThemedText.Body color={page === maxPage ? 'textDisabled' : 'primary'}> {'>'} </ThemedText.Body>
              </Arrow>
            </PageButtons>
          </Container>
        ) : (
          <Message>
            <AlertTriangle size={16} color="#FF8F00" />
            <ThemedText.SubHeader ml="4px" color="#FF8F00">
              <Trans>There are no matches to bet on</Trans>
            </ThemedText.SubHeader>
          </Message>
        )}

        <SportBetModal
          isOpen={betModalIsOpen}
          titleEvent={scheduleItem?.provider.sportType || ''}
          dateEvent={scheduleItem?.startTime || ''}
          homeTeam={scheduleItem?.homeTeam || ''}
          awayTeam={scheduleItem?.awayTeam || ''}
          onDismiss={closeBetModal}
          itemEvent={scheduleItem}
          onTeamSelect={betPosition}
        />
      </Container>
    </>
  )
}

export default Events
